/**
 * ── O MOTOR DE TRADUÇÃO DA TELA DO ALUNO ──────────────────────────────────
 *
 * A MESMA escada do servidor (`nucleo.ts`), com outra base:
 *
 * ```
 * motor.ts  ->  base = `pt`, as 533 chaves        (servidor, Node)
 * tela.ts   ->  base = `CASCA`, as 100 da moldura (+ as folhas que descem)
 * ```
 *
 * ## Por que o aluno não pode usar o `motor.ts`
 *
 * Porque ele importa `pt` com as 533 chaves, e o pedaço de entrada do aluno é
 * contado ao byte. A medição que abriu esta separação: das 533, o
 * `index-*.js` **lia 100** — as outras 433 (~27 kB de fonte) viajavam em toda
 * visita para serem usadas por telas que descem por `import()`, ou por nenhuma
 * tela, porque nascem no servidor.
 *
 * ## O que a tela TEM de fazer, e o que acontece se esquecer
 *
 * Toda tela do aluno desce junto com a folha do conteúdo e registra-a:
 * `telaComDicionario` em `App.tsx` é o único lugar que o faz, e faz por todas.
 * Enquanto a folha não está registada, uma chave de conteúdo cai na **própria
 * chave** — `sala.concluir` à vista. É deliberadamente feio, e é por isso que
 * a cerca `que-chave-vive-em-que-pedaco` mede, no artefacto, que nenhum pedaço
 * lê chave que a sua base não cobre.
 *
 * ## `Chave` e `Tradutor` continuam a ser os do `motor.ts`
 *
 * Eles são **tipos**, e um `import type` desaparece no build — o tipo continua
 * a sair do dicionário completo, logo escrever `t('comunidade.x')` numa tela
 * continua a ser conferido pelo `tsc` contra as 533 chaves. O que não desce é
 * o VALOR.
 */

import { CASCA } from './casca.js';
import { criarMotor, type Folha, type Valores } from './nucleo.js';

import type { Chave, DicionarioParcial, Tradutor } from './motor.js';

export { carregarDicionario } from './carregadores.js';
export type { Chave, DicionarioParcial, Tradutor } from './motor.js';
export type { Valores } from './nucleo.js';

const motor = criarMotor(CASCA);

/**
 * Acrescenta uma folha ao fallback — o que a tela faz com o conteúdo que
 * desceu com ela. Idempotente: toda tela registra a mesma folha.
 */
export function registarFolha(folha: Folha): void {
  motor.registarFolha(folha);
}

/** As chaves que a tela conhece agora: a casca mais as folhas já registadas. */
export function chavesConhecidas(): string[] {
  return motor.chavesConhecidas();
}

/**
 * A frase, na língua já carregada.
 *
 * A escada é **língua pedida → casca → folhas registadas → a própria chave**.
 */
export function traduzir(
  dicionario: DicionarioParcial | null | undefined,
  chave: Chave,
  valores?: Valores,
): string {
  return motor.traduzir(dicionario as Folha | null | undefined, chave, valores);
}

export function tradutorDe(dicionario: DicionarioParcial | null | undefined): Tradutor {
  return (chave, valores) => traduzir(dicionario, chave, valores);
}
